import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';
import { 
  FiGlobe, 
  FiPlus, 
  FiDatabase, 
  FiCalendar, 
  FiDollarSign, 
  FiUsers, 
  FiSettings,
  FiFileText,
  FiMessageSquare,
  FiGrid,
  FiClock,
  FiLayers,
  FiMenu,
  FiX
} from 'react-icons/fi';
import LogoImage from './LogoImage';

const Sidebar = ({ isOpen, onClose }) => {
  const { user, isGlobalAdmin, isBusinessAdmin } = useSimpleAuth();
  const location = useLocation();
  const [pinned, setPinned] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);

  if (!user) return null;

  const isActive = (path) => location.pathname === path;

  const isDesktop = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 1024px)').matches;
  const expandedDesktop = pinned || hovered;

  const NavItem = ({ to, icon: Icon, label, badge }) => (
    <Link
      to={to}
      onClick={onClose}
      className={`
        sidebar-nav-item group relative
        ${isActive(to) ? 'active' : ''}
        ${isDesktop && !expandedDesktop ? 'justify-center px-3' : ''}
      `}
    >
      <Icon className={`icon flex-shrink-0 ${isDesktop && !expandedDesktop ? 'w-5 h-5' : 'w-5 h-5'}`} />
      <span className={`transition-opacity duration-200 ${isDesktop && !expandedDesktop ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
        {label}
      </span>
      {badge && (
        <span className="ml-auto bg-primary-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
          {badge}
        </span>
      )}
      {/* Tooltip for collapsed state */}
      {isDesktop && !expandedDesktop && (
        <div className="absolute left-full ml-2 px-3 py-1.5 bg-surface-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 shadow-lg">
          {label}
        </div>
      )}
    </Link>
  );

  const NavSection = ({ title, children }) => (
    <div className="mb-6">
      {title && (
        <h3 className={`px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-white/40 ${isDesktop && !expandedDesktop ? 'sr-only' : ''}`}>
          {title}
        </h3>
      )}
      <div className="space-y-1">
        {children}
      </div>
    </div>
  );

  return (
    <>
      {/* Overlay para móvil */}
      {!isDesktop && isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden transition-opacity duration-300"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-full z-40
          sidebar-premium
          transform transition-all duration-300 ease-out
          ${isDesktop ? 'translate-x-0' : (isOpen ? 'translate-x-0' : '-translate-x-full')}
          ${isDesktop ? (expandedDesktop ? 'w-64' : 'w-[72px]') : 'w-72'}
          shadow-2xl
        `}
        onMouseEnter={() => setHovered(true)} 
        onMouseLeave={() => setHovered(false)}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className={`p-4 border-b border-white/10 ${isDesktop && !expandedDesktop ? 'px-3' : ''}`}>
            <div className="flex items-center justify-between">
              <div className={`flex items-center gap-3 ${isDesktop && !expandedDesktop ? 'justify-center w-full' : ''}`}>
                <div className="relative">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg shadow-primary-500/25">
                    <LogoImage className="h-6 w-6 object-contain rounded" fallback={
                      <span className="text-white font-bold text-lg">R</span>
                    } />
                  </div>
                </div>
                <div className={`transition-opacity duration-200 ${isDesktop && !expandedDesktop ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                  <h2 className="text-lg font-bold text-white tracking-tight">Reserberry</h2>
                  <p className="text-xs text-white/50">{isGlobalAdmin ? 'Panel Global' : 'Panel Admin'}</p>
                </div>
              </div>
              
              {/* Pin button - solo en desktop expandido */}
              {isDesktop && expandedDesktop && (
                <button
                  onClick={() => setPinned(v => !v)}
                  className={`
                    p-2 rounded-lg transition-colors
                    ${pinned ? 'bg-primary-500/20 text-primary-400' : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80'}
                  `}
                  title={pinned ? 'Desfijar menú' : 'Fijar menú'}
                >
                  {pinned ? <FiX className="w-4 h-4" /> : <FiMenu className="w-4 h-4" />}
                </button>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 py-4 overflow-y-auto overflow-x-hidden ${isDesktop && !expandedDesktop ? 'px-2' : 'px-3'}`}>
            {isGlobalAdmin ? (
              <>
                <NavSection>
                  <NavItem to="/global-admin" icon={FiGlobe} label="Panel Global" />
                  <NavItem to="/register-business" icon={FiPlus} label="Crear Negocio" />
                  <NavItem to="/migration" icon={FiDatabase} label="Migración" />
                </NavSection>
              </>
            ) : isBusinessAdmin ? (
              <>
                <NavSection title="Principal">
                  <NavItem to="/admin/day" icon={FiGrid} label="Dashboard" />
                  <NavItem to="/admin/appointments" icon={FiCalendar} label="Citas" />
                </NavSection>

                <NavSection title="Gestión">
                  <NavItem to="/admin/users" icon={FiUsers} label="Usuarios" />
                  <NavItem to="/admin/employees" icon={FiUsers} label="Empleados" />
                  <NavItem to="/admin/services" icon={FiLayers} label="Servicios" />
                </NavSection>

                <NavSection title="Planificación">
                  <NavItem to="/admin/cuadrante" icon={FiCalendar} label="Cuadrante" />
                  <NavItem to="/admin/date-control" icon={FiClock} label="Control Fechas" />
                </NavSection>

                <NavSection title="Finanzas">
                  <NavItem to="/admin/accounting" icon={FiDollarSign} label="Contabilidad" />
                  <NavItem to="/admin/reports" icon={FiFileText} label="Informes" />
                </NavSection>

                <NavSection title="Sistema">
                  <NavItem to="/admin/config" icon={FiSettings} label="Configuración" />
                  <NavItem to="/support" icon={FiMessageSquare} label="Soporte" />
                </NavSection>
              </>
            ) : null}
          </nav>

          {/* Footer */}
          <div className={`p-4 border-t border-white/10 ${isDesktop && !expandedDesktop ? 'px-2' : ''}`}>
            <div className={`flex items-center gap-3 ${isDesktop && !expandedDesktop ? 'justify-center' : ''}`}>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-white/20 to-white/5 flex items-center justify-center text-white/80 font-medium text-sm border border-white/10">
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className={`flex-1 min-w-0 transition-opacity duration-200 ${isDesktop && !expandedDesktop ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                <p className="text-sm font-medium text-white truncate">
                  {user?.displayName || user?.email?.split('@')[0] || 'Usuario'}
                </p>
                <p className="text-xs text-white/50 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
